import { useEffect, useState } from 'react'

import { Field, Form, Formik, FormikValues } from 'formik'
import { useIntl } from 'react-intl'

import { Box, Button, THEME, ThemeProvider, Typography, useTheme } from '@themenu/design-system'

import { FormikFieldInput } from '../../components/Form/FormikFieldInput'
import { FormikFieldInputPassword } from '../../components/Form/FormikFieldInputPassword'
import { PushButtonToBottom, StyledFormContainer } from '../../components/Shared/Form'
import { Title } from '../../components/Shared/Title'
import { useCredentialsFormValidators } from '../../hooks/Form/useCredentialsFormValidators'
import { useDebounce } from '../../hooks/useDebounce'
import { isSso } from '../../services/api/auth'
import { environmentVariables } from '../../utils/environmentVariables'
import { isValidEmail } from '../../utils/isValidEmail'

export type CredentialsFormValues = {
  username: string
  password: string
  isSso: boolean
}

export type CredentialsFormProps = {
  enableSso: boolean
  onSubmit: (values: CredentialsFormValues) => Promise<void>
}

export const CredentialsForm = ({ onSubmit, enableSso }: CredentialsFormProps): JSX.Element | null => {
  const intl = useIntl()
  const theme = useTheme()

  const [isUserSSO, setIsUserSSO] = useState(false)
  const { initialValues, validationSchema } = useCredentialsFormValidators(isUserSSO)
  const [username, setUsername] = useState('')

  const usernameDebounced = useDebounce(username, 100)

  useEffect(() => {
    if (enableSso && username && isValidEmail(username)) {
      isSso(username).then((isSso) => setIsUserSSO(isSso))
    } else {
      setIsUserSSO(false)
    }
  }, [usernameDebounced]) // eslint-disable-line react-hooks/exhaustive-deps

  const onFormSubmit = (values: FormikValues): Promise<void> => {
    return onSubmit({
      username: values.username,
      password: values.password,
      isSso: isUserSSO,
    })
  }

  return (
    <Box mt={24} display="flex" flex="1" flexDirection="column">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form>
            <StyledFormContainer>
              <Title>
                {intl.formatMessage({
                  id: 'sign_in_page.form.title',
                })}
              </Title>

              <ThemeProvider theme={THEME.LIGHT}>
                <Field
                  mt={16}
                  name="username"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  spellCheck="false"
                  autoComplete="off"
                  component={FormikFieldInput}
                  forceValidation={isUserSSO}
                  label={intl.formatMessage({
                    id: 'sign_in.form.username',
                  })}
                  labelOption="sticky"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('username', event.target.value)
                    setUsername(event.target.value)
                  }}
                />

                {isUserSSO ? null : (
                  <Box display="flex" flexDirection="column">
                    <Field
                      mt={8}
                      name="password"
                      type="password"
                      spellCheck="false"
                      autoComplete="off"
                      component={FormikFieldInputPassword}
                      label={intl.formatMessage({
                        id: 'sign_in.form.password',
                      })}
                      labelOption="sticky"
                    />
                    <ThemeProvider theme={THEME.DARK}>
                      <Typography
                        variant="footnote"
                        component="a"
                        href={environmentVariables('FORGOT_PASSWORD_URL')}
                        color={theme.colors.content.subdued}
                        p={4}
                        tabIndex={-1}
                      >
                        {intl.formatMessage({ id: 'sign_in.form.forgotten_password' })}
                      </Typography>
                    </ThemeProvider>
                  </Box>
                )}
                <PushButtonToBottom>
                  <Button aria-label="submit" type="submit" variant="contrast" fullWidth disabled={isSubmitting}>
                    {intl.formatMessage({
                      id: isUserSSO ? 'sign_in_page.form.submit_sso' : 'sign_in_page.form.submit',
                    })}
                  </Button>
                </PushButtonToBottom>
              </ThemeProvider>
            </StyledFormContainer>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
/* eslint-enable jsx-a11y/no-autofocus */
